import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import {
  Smartphone,
  Monitor,
  Apple,
  Share,
  MoreVertical,
  PlusSquare,
  Download,
  Chrome,
  Globe,
  ArrowDown,
  CheckCircle,
  X
} from 'lucide-react';

const InstallAppGuide = ({ open, onClose }) => {
  const [showHindi, setShowHindi] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Download className="w-6 h-6 text-blue-600" />
            Install Factory App
          </DialogTitle>
          <DialogDescription>
            Install this app on your device for quick access anytime!
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="android" className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="android" className="flex items-center gap-2" data-testid="android-tab">
              <Smartphone className="w-4 h-4" />
              Android
            </TabsTrigger>
            <TabsTrigger value="ios" className="flex items-center gap-2" data-testid="ios-tab">
              <Apple className="w-4 h-4" />
              iPhone
            </TabsTrigger>
            <TabsTrigger value="desktop" className="flex items-center gap-2" data-testid="desktop-tab">
              <Monitor className="w-4 h-4" />
              Desktop
            </TabsTrigger>
          </TabsList>

          {/* Android Instructions */}
          <TabsContent value="android" className="mt-4 space-y-4">
            {/* Language Toggle for Android - Left aligned with highlight */}
            <div className="flex justify-start">
              <Button 
                variant={showHindi ? "default" : "outline"}
                size="sm" 
                onClick={() => setShowHindi(!showHindi)}
                className={showHindi 
                  ? "bg-orange-500 hover:bg-orange-600 text-white font-semibold" 
                  : "bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300 font-semibold"
                }
              >
                {showHindi ? '🇬🇧 Switch to English' : '🇮🇳 हिंदी में देखें (View in Hindi)'}
              </Button>
            </div>

            {!showHindi ? (
              /* English Instructions */
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 space-y-4">
                  <h3 className="font-bold text-lg flex items-center gap-2 text-green-800">
                    <Smartphone className="w-5 h-5" />
                    Android Installation (Chrome Browser)
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Step 1 */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Open in Chrome Browser</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Chrome className="w-4 h-4 text-blue-500" />
                          Open this website in Google Chrome
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Tap the Menu Button</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <MoreVertical className="w-4 h-4" />
                          Tap the <strong>three dots</strong> (⋮) in the top-right corner
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Select "Add to Home Screen"</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <PlusSquare className="w-4 h-4 text-green-600" />
                          Find and tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        4
                      </div>
                      <div>
                        <p className="font-medium">Tap "Add" or "Install"</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Confirm by tapping <strong>"Add"</strong> or <strong>"Install"</strong>
                        </p>
                      </div>
                    </div>

                    {/* Step 5 */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm border-2 border-green-400">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        ✓
                      </div>
                      <div>
                        <p className="font-medium text-green-700">Done! App is installed</p>
                        <p className="text-sm text-gray-600">
                          Find the app icon on your home screen 📱
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Hindi Instructions */
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4 space-y-4">
                  <h3 className="font-bold text-lg flex items-center gap-2 text-orange-800">
                    <Smartphone className="w-5 h-5" />
                    Android इंस्टॉलेशन (Chrome Browser)
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Step 1 Hindi */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                        १
                      </div>
                      <div>
                        <p className="font-medium">Chrome Browser में खोलें</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Chrome className="w-4 h-4 text-blue-500" />
                          इस वेबसाइट को Google Chrome में खोलें
                        </p>
                      </div>
                    </div>

                    {/* Step 2 Hindi */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                        २
                      </div>
                      <div>
                        <p className="font-medium">Menu Button पर टैप करें</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <MoreVertical className="w-4 h-4" />
                          ऊपर दाईं तरफ <strong>तीन बिंदु</strong> (⋮) पर टैप करें
                        </p>
                      </div>
                    </div>

                    {/* Step 3 Hindi */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                        ३
                      </div>
                      <div>
                        <p className="font-medium">"होम स्क्रीन में जोड़ें" चुनें</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <PlusSquare className="w-4 h-4 text-orange-600" />
                          <strong>"Add to Home screen"</strong> या <strong>"Install app"</strong> पर टैप करें
                        </p>
                      </div>
                    </div>

                    {/* Step 4 Hindi */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                        ४
                      </div>
                      <div>
                        <p className="font-medium">"Add" या "Install" पर टैप करें</p>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-orange-600" />
                          <strong>"Add"</strong> या <strong>"Install"</strong> पर टैप करके confirm करें
                        </p>
                      </div>
                    </div>

                    {/* Step 5 Hindi */}
                    <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm border-2 border-orange-400">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                        ✓
                      </div>
                      <div>
                        <p className="font-medium text-orange-700">हो गया! App इंस्टॉल हो गया</p>
                        <p className="text-sm text-gray-600">
                          अपनी होम स्क्रीन पर app का icon देखें 📱
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* iOS Instructions */}
          <TabsContent value="ios" className="mt-4 space-y-4">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 space-y-4">
                <h3 className="font-bold text-lg flex items-center gap-2 text-blue-800">
                  <Apple className="w-5 h-5" />
                  iPhone Installation (Safari Browser)
                </h3>
                
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-medium">Open in Safari Browser</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Globe className="w-4 h-4 text-blue-500" />
                        Open this website in <strong>Safari</strong> (not Chrome)
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-medium">Tap the Share Button</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Share className="w-4 h-4 text-blue-600" />
                        Tap the <strong>Share button</strong> (□↑) at the bottom of the screen
                      </p>
                      <div className="mt-2 p-2 bg-gray-100 rounded text-center">
                        <span className="text-2xl">⬆️📤</span>
                        <p className="text-xs text-gray-500">Share button looks like this</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-medium">Scroll Down & Tap "Add to Home Screen"</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <PlusSquare className="w-4 h-4 text-blue-600" />
                        Scroll down and find <strong>"Add to Home Screen"</strong>
                      </p>
                      <div className="mt-2 p-2 bg-gray-100 rounded text-center">
                        <span className="text-2xl">➕🏠</span>
                        <p className="text-xs text-gray-500">Add to Home Screen</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <p className="font-medium">Tap "Add" in the Top-Right</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                        Tap <strong>"Add"</strong> in the top-right corner
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm border-2 border-blue-400">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      ✓
                    </div>
                    <div>
                      <p className="font-medium text-blue-700">Done! App is installed</p>
                      <p className="text-sm text-gray-600">
                        Find the app icon on your home screen 📱
                      </p>
                    </div>
                  </div>
                </div>

                {/* Important Note for iOS */}
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800 flex items-start gap-2">
                    <span className="text-lg">⚠️</span>
                    <span>
                      <strong>Important:</strong> On iPhone, you MUST use Safari browser. 
                      The "Add to Home Screen" option is not available in Chrome or other browsers on iOS.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Desktop Instructions */}
          <TabsContent value="desktop" className="mt-4 space-y-4">
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 space-y-4">
                <h3 className="font-bold text-lg flex items-center gap-2 text-purple-800">
                  <Monitor className="w-5 h-5" />
                  Desktop Installation (Chrome/Edge Browser)
                </h3>
                
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-medium">Open in Chrome or Edge Browser</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Chrome className="w-4 h-4 text-blue-500" />
                        Open this website in Google Chrome or Microsoft Edge
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-medium">Look for Install Icon in Address Bar</p>
                      <p className="text-sm text-gray-600">
                        Look for the <strong>install icon</strong> (⊕) in the address bar (right side)
                      </p>
                      <div className="mt-2 p-2 bg-gray-100 rounded flex items-center gap-2 justify-center">
                        <div className="flex items-center bg-white border rounded px-3 py-1">
                          <span className="text-gray-400 text-sm">🔒 factory-workflow.com</span>
                          <span className="ml-2 text-xl">⊕</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-medium">Click "Install"</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Download className="w-4 h-4 text-purple-600" />
                        Click the install icon and then click <strong>"Install"</strong>
                      </p>
                    </div>
                  </div>

                  {/* Alternative Method */}
                  <div className="p-3 bg-white rounded-lg shadow-sm border-l-4 border-purple-400">
                    <p className="font-medium text-purple-700 mb-2">Alternative Method:</p>
                    <p className="text-sm text-gray-600">
                      Click the <strong>three dots menu</strong> (⋮) → Select <strong>"Install Factory App"</strong> or <strong>"Save and Share" → "Install"</strong>
                    </p>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm border-2 border-purple-400">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      ✓
                    </div>
                    <div>
                      <p className="font-medium text-purple-700">Done! App is installed</p>
                      <p className="text-sm text-gray-600">
                        Find the app in your Start Menu or Desktop 💻
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Close Button */}
        <div className="mt-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallAppGuide;
